import { MaxSizeValidator } from '@angular-material-components/file-input';
import { EventEmitter, Output } from '@angular/core';
import { Component, OnInit, Input } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { MatChipInputEvent } from '@angular/material/chips';
import { functonLabel, ILabel, ICustomer, IMachineForSettings, MachineFunction } from 'app/models';

import { ImageService, MachineService } from 'app/services';
import { MessageService } from 'app/services/message/message.service';
import { ENTER, COMMA } from '@angular/cdk/keycodes';

@Component({
  selector: 'app-machine-mngt-detail',
  templateUrl: './machine-mngt-detail.component.html',
  styleUrls: ['./machine-mngt-detail.component.scss'],
})
export class MachineMngtDetailComponent implements OnInit {
  @Input()
  machines: IMachineForSettings[] = [];

  @Input()
  customers: ICustomer[] = [];

  @Output()
  machineSaved = new EventEmitter<boolean>();

  readonly functions: MachineFunction[] = Object.values(MachineFunction);
  readonly functonLabel = functonLabel;
  readonly imgaMaxSize = 2; //Mb

  machineForm: FormGroup = this.formBuilder.group(
    {
      id: undefined,
      serialNumber: [null],
      name: [null],
      customerId: [null],
      function: [null],
      operator: [null],
      year: [null],
      machineType: [null],
      imageBin: [null],
      deleteImg: [null],
      labels: [null],
    },
    {}
  );

  serialNumbers!: string[];

  addOnBlur = true;

  readonly separatorKeysCodes = [ENTER, COMMA] as const;

  constructor(
    private formBuilder: FormBuilder,
    private machineService: MachineService,
    private messageService: MessageService,
    private imageService: ImageService
  ) {}

  ngOnInit(): void {
    this.machineService.findAllMachinesForSettings().subscribe(
      (machineList) => {
        this.serialNumbers = machineList.map((item) => item.serialNumber);
        this.initEmpty();
      },
      () =>
        this.messageService.showDirectSimpleMessage(
          ` Loading machines serials error `
        )
    );
  }

  initEmpty() {
    this.machineForm = this.formBuilder.group(
      {
        id: undefined,
        serialNumber: [
          null,
          {
            validators: [Validators.required],
          },
        ],
        name: [null, Validators.required],
        customerId: [null, Validators.required],
        function: [null, Validators.required],
        operator: [null],
        year: [null],
        machineType: [null],
        imageBin: [null, MaxSizeValidator(this.imgaMaxSize * 2 ** 20)],
        imageStr: [null],
        deleteImg: [false],
        labels: [null],
      },
      { validators: this.uniqueSerialNumberValidator }
    );
  }

  initEdit(currentMachine: IMachineForSettings) {
    this.machineForm.setValue({
      id: currentMachine.id,
      serialNumber: currentMachine.serialNumber,
      name: currentMachine.name,
      customerId: currentMachine.customerId,
      function: currentMachine.function,
      operator: currentMachine.operator,
      year: currentMachine.year,
      machineType: currentMachine.machineType,
      imageBin: null,
      imageStr: null,
      deleteImg: null,
      labels: currentMachine.labels,
    });
  }

  uniqueSerialNumberValidator: ValidatorFn = (
    control: AbstractControl
  ): ValidationErrors | null => {
    const id = control.get('id')?.value;
    const serial = control.get('serialNumber')?.value;

    return !id && this.serialNumbers.includes(serial)
      ? { serialNumberTaken: true }
      : null;
  };

  clear(): void {
    this.machineForm.reset();
  }

  async onSubmit() {
    if (!this.machineForm || this.machineForm.invalid) {
      return;
    }

    if (this.machineForm.value.imageBin) {
      await this.imageService
        .encodeB64(this.machineForm.value.imageBin)
        .then((encodedValue) => {
          this.machineForm.patchValue({
            imageStr: encodedValue,
          });
        });
    }

    if (this.machineForm.value.id) {
      this.machineService
        .updateMachine(
          this.machineForm.value.id,
          this.machineForm.getRawValue()
        )
        .subscribe(
          (): void => {
            this.machineSaved.emit(true);
            this.clear();
          },
          () =>
            this.messageService.showDirectSimpleMessage(` Machine save Error`)
        );
      return;
    }
    this.machineService.addMachine(this.machineForm.getRawValue()).subscribe(
      (): void => {
        this.machineSaved.emit(true);
        this.clear();
      },
      () => this.messageService.showDirectSimpleMessage(` Machine save Error`)
    );
  }

  add(event: MatChipInputEvent): void {
    const value = (event.value || '').trim();
    if(value === '') {
      return;
    }

    const values = value.split(':');
    let labelToAdd;
    switch(values.length){
      case 1:
        labelToAdd = {
          type: 'DEP',
          code: values[0],
        };
        break;
      case 2:
        labelToAdd = {
          type: values[0],
          code: values[1],
        };
        break;
      case 3:
      default:
        labelToAdd = {
          type: values[0],
          code: values[1],
          description: values[2],
        };
    }
    let currentLabels = this.machineForm.value.labels;
    if(currentLabels === null){
      currentLabels = [];
    }
    currentLabels.push(labelToAdd);
    this.machineForm.setValue({
      ...this.machineForm.value,
      labels: currentLabels,
    });

    // Clear the input value
     if (event.input) {
      event.input.value = '';
    }
  }

  remove(label: ILabel): void {
    const currentLabels = this.machineForm.value.labels;
    const index = currentLabels.indexOf(label);
    if (index >= 0) {
      currentLabels.splice(index, 1);
    }
  }
}
